import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Logo from "../../components/Logo";
import { api } from "../../lib/api";
import {
  LayoutDashboard, Home as HomeIcon, User, Briefcase, FolderKanban,
  MailIcon, LogOut, ExternalLink, Menu, X, BarChart3,
} from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/home", label: "Branding / Hero", icon: HomeIcon },
  { to: "/dashboard/about", label: "About", icon: User },
  { to: "/dashboard/services", label: "Services", icon: Briefcase },
  { to: "/dashboard/portfolio", label: "Portfolio", icon: FolderKanban },
  { to: "/dashboard/contact", label: "Contact & Social", icon: MailIcon },
  { to: "/dashboard/analytics", label: "Analytics & Scripts", icon: BarChart3 },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(undefined);

  useEffect(() => {
    api.get("/content").then((r) => setLogoUrl(r.data?.logo_url)).catch(() => {});
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-[#0B1020] text-white" data-testid="admin-layout">
      {/* Mobile topbar */}
      <div className="fixed inset-x-0 top-0 z-40 flex items-center justify-between border-b border-white/10 bg-[#0B1020]/80 px-4 py-3 backdrop-blur md:hidden">
        <Logo size={28} src={logoUrl} />
        <button onClick={() => setMobileOpen((v) => !v)} data-testid="admin-mobile-toggle">
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 flex-col border-r border-white/10 bg-[#0F1528] p-6 md:static md:flex ${
          mobileOpen ? "flex" : "hidden md:flex"
        }`}
      >
        <div className="mb-10 mt-10 md:mt-0">
          <Logo size={32} src={logoUrl} />
        </div>
        <nav className="flex-1 space-y-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setMobileOpen(false)}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-gradient-to-r from-fuchsia-500/15 via-fuchsia-500/5 to-transparent text-white"
                    : "text-white/60 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon size={16} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-6 space-y-2 border-t border-white/10 pt-6">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
            ) : (
              <div className="h-9 w-9 rounded-full gradient-bg" />
            )}
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-white" data-testid="admin-user-name">{user?.name}</div>
              <div className="truncate text-xs text-white/50">{user?.email}</div>
            </div>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs text-white/60 hover:bg-white/5 hover:text-white"
            data-testid="admin-view-site"
          >
            <ExternalLink size={14} /> View site
          </a>
          <button
            onClick={doLogout}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs text-white/60 hover:bg-white/5 hover:text-white"
            data-testid="admin-logout-btn"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-6 pt-20 md:p-10 md:pt-10">
        <Outlet />
      </main>
    </div>
  );
}
